import React, { useCallback, useId, useState } from 'react';
import { Link } from 'react-router-dom';
import PremiumBadge from './PremiumBadge';
import type { PremiumCapability } from './PremiumFeatureGate';
import type { PremiumFeatureKey } from '../../constants/premium';
import { PREMIUM_FEATURE_LABELS } from '../../constants/premium';
import { Button } from '../ui/Button';
import apiService from '../../services/ApiService';

type PremiumLockedSectionProps = {
  /** When true, user has entitlement — render children normally. */
  entitled: boolean;
  capability: PremiumCapability;
  featureKey: PremiumFeatureKey;
  title: string;
  description: string;
  /** Short helper shown under locked controls */
  helperText?: string;
  children: React.ReactNode;
  className?: string;
  /** Surface for analytics, e.g. maintenance_panel */
  surface?: string;
};

/**
 * Visible Premium-gated block: subdued when locked, vivid badge, contextual explain on interaction.
 */
export const PremiumLockedSection: React.FC<PremiumLockedSectionProps> = ({
  entitled,
  capability,
  featureKey,
  title,
  description,
  helperText,
  children,
  className = '',
  surface = 'unknown',
}) => {
  const labelId = useId();
  const [sheetOpen, setSheetOpen] = useState(false);
  const featureLabel = PREMIUM_FEATURE_LABELS[featureKey] ?? title;

  const openExplain = useCallback(() => {
    if (entitled) return;
    setSheetOpen(true);
    void apiService.trackEvent('premium_gate_opened', {
      metadata: { feature_key: featureKey, capability, surface },
    });
  }, [capability, entitled, featureKey, surface]);

  if (entitled) {
    return <div className={className}>{children}</div>;
  }

  return (
    <div className={`relative ${className}`.trim()}>
      <div
        className="rounded-xl border border-white/[0.08] bg-white/[0.02] p-4"
        aria-labelledby={labelId}
      >
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <h4 id={labelId} className="text-sm font-semibold text-zinc-200">
            {title}
          </h4>
          <PremiumBadge featureLabel={featureLabel} variant="compact" />
        </div>
        <p className="mb-3 text-xs leading-relaxed text-zinc-500">{description}</p>
        <div
          className="premium-locked-surface rounded-lg border border-white/[0.06] bg-black/30 p-3 [&_input]:cursor-not-allowed [&_textarea]:cursor-not-allowed [&_button:not(.premium-unlock-cta)]:pointer-events-none [&_button:not(.premium-unlock-cta)]:opacity-60"
          onClick={(e) => {
            const target = e.target as HTMLElement;
            if (target.closest('button, input, textarea, select, a')) {
              openExplain();
            }
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') openExplain();
          }}
          role="group"
          aria-label={`${featureLabel}. Premium feature. Upgrade to use.`}
        >
          {children}
        </div>
        {helperText ? (
          <p className="mt-2 text-xs text-zinc-500">{helperText}</p>
        ) : null}
        <Button
          type="button"
          variant="secondary"
          size="sm"
          className="premium-unlock-cta mt-3"
          onClick={openExplain}
        >
          Learn about Premium
        </Button>
      </div>

      {sheetOpen ? (
        <div
          className="fixed inset-0 z-[120] flex items-end justify-center bg-black/70 p-4 sm:items-center"
          role="dialog"
          aria-modal="true"
          aria-labelledby={`${labelId}-sheet-title`}
          onClick={() => setSheetOpen(false)}
        >
          <div
            className="w-full max-w-md rounded-xl border border-orange-500/25 bg-[#111] p-5 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-2 flex items-center gap-2">
              <h3 id={`${labelId}-sheet-title`} className="text-base font-semibold text-white">
                {title}
              </h3>
              <PremiumBadge featureLabel={featureLabel} />
            </div>
            <p className="text-sm leading-relaxed text-zinc-400">{description}</p>
            <p className="mt-3 text-sm text-zinc-500">Included with Bilt:Spec Premium.</p>
            <div className="mt-5 flex flex-wrap gap-3">
              <Link to="/settings/billing" onClick={() => setSheetOpen(false)}>
                <Button type="button" size="sm">
                  Try Premium
                </Button>
              </Link>
              <Button type="button" variant="ghost" size="sm" onClick={() => setSheetOpen(false)}>
                Not now
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default PremiumLockedSection;
